import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";

/** The browser's IANA time zone (e.g. "America/Toronto"); "" when unknown (server = UTC). */
const aiTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

/*
 * AI features ("Write with AI", "Catch me up", help chat + "Report a problem",
 * "Ask ProCharted" in the top search box). All request/response shapes
 * for backend `api/ai` live here so they are easy to align with the backend.
 */

export type AiStatus = {
  /** The server has an AI key set up. */
  configured: boolean;
  /** The firm switched AI features on (Settings > Firm Details). */
  enabled: boolean;
  /** The signed-in person may switch AI on/off for the firm. */
  canManage: boolean;
  limitPerDay: number;
  usedToday: number;
};

export type AiWriteAction =
  | "draft"
  | "improve"
  | "shorten"
  | "friendlier"
  | "formal"
  | "fix"
  /** Emails only: returns just `subject`. */
  | "subject";

export type AiPurpose = "email" | "note";

export type AiWriteRequest = {
  orgId: string;
  action: AiWriteAction;
  text?: string;
  instruction?: string;
  purpose?: AiPurpose;
  subject?: string;
  workId?: string;
  customerId?: string;
  /** Email being answered (compose replies), sent as data. */
  replyTo?: AiEmailMessage;
};

/** `text` is plain text: blank line between paragraphs, bullets start "- ". */
export type AiWriteResponse = { text: string; subject?: string };

export type AiWorkSummaryResponse = { summary: string; generatedAt: string };

export type AiHelpChatMessage = { role: "user" | "assistant"; text: string };

export type AiHelpChatRequest = {
  orgId: string;
  /** Oldest first, at most the last 20 messages. */
  messages: AiHelpChatMessage[];
  /** Current page (address without query string or #). */
  page: string;
};

export type AiHelpLink = { title: string; url: string };

/** `reply`: plain text with "1. " steps, "- " bullets and **bold** labels. */
export type AiHelpChatResponse = {
  reply: string;
  suggestReport: boolean;
  links: AiHelpLink[];
};

export type AiBugReportRequest = {
  orgId: string;
  whatHappened: string;
  expected?: string;
  page?: string;
  chatExcerpt?: string;
  browser?: string;
  appVersion?: string;
};

export type AiBugReportResponse = {
  reportId: string;
  /** Short report number to show, e.g. "K7M3Q9TX" (Roadmap 1856; older servers don't send it). */
  reportCode?: string;
  emailed: boolean;
  savedToSheet: boolean;
};

export type AiAskItemKind = "work" | "client" | "invoice" | "person";

/** "Ask ProCharted" (top search box): a question about the firm's data. */
export type AiAskRequest = {
  orgId: string;
  question: string;
  // Browser time zone: the server uses it for "today", "overdue" and dates.
  timeZone?: string;
  /** Earlier questions/answers of the open Ask window (follow-ups). */
  history?: { question: string; answer: string }[];
};

export type AiAskItem = {
  kind: AiAskItemKind;
  id: string;
  title: string;
  subtitle?: string;
  /** In-app address, e.g. "/work/123" (anything else is ignored). */
  link: string;
};

/** `answer`: plain text like the help chat (steps, "- " bullets, **bold**). */
export type AiAskResponse = {
  answer: string;
  items: AiAskItem[];
  /** Matches not listed in `items`. */
  moreCount: number;
  openLink?: { label: string; url: string };
  /** An action the AI prepared (nothing is created until the person clicks). */
  proposal?: AiProposal | null;
  /** A chart of numbers from this answer's lookups (server-checked). */
  chart?: AiAskChart | null;
};

/** Ask ProCharted chart (AI reporting, owner 2026-10-25). */
export type AiAskChart = {
  kind: "bar" | "stacked_bar" | "line";
  title: string;
  unit: "hours" | "money" | "count" | "days" | "percent";
  series: string[];
  points: { label: string; values: number[] }[];
};

/** A person or client the AI matched, e.g. an assignee or a duplicate. */
export type AiProposalRef = {
  id: string;
  name: string;
  detail?: string;
  link?: string;
};

export type AiWorkProposalFields = {
  customerId?: string;
  customerName?: string;
  customerType?: "organization" | "individual";
  serviceId?: string;
  workTypeName?: string;
  name?: string;
  /** YYYY-MM-DD */
  startDate?: string;
  /** YYYY-MM-DD */
  dueDate?: string;
  assignedUserIds?: string[];
  assignees?: AiProposalRef[];
  roleId?: string | null;
  roleName?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  timeBudget?: number | null;
  notes?: string | null;
  initialStatus?: "Ready" | "Scheduled";
  /** New work window's optional sections (they start only on Create). */
  repeat?: AiRepeatProposal | null;
  automation?: AiAutomationProposal | null;
  sequence?: AiSequenceProposal | null;
};

/** "Add a repeat schedule": day settings come from the work's dates. */
export type AiRepeatProposal = {
  frequency?: string;
  every?: number;
  createMode?: string;
  /** YYYY-MM-DD */
  repeatUntil?: string;
  label?: string;
};

/** "Add an automation" (same shape as the window's AutomationDraft). */
export type AiAutomationProposal = {
  conditionId?: string;
  conditionKey?: string;
  conditionName?: string;
  conditionValue?: string;
  conditionValueName?: string | null;
  conditionExtraValue?: string;
  actionId?: string;
  actionKey?: string;
  actionName?: string;
  actionValue?: string;
  actionValueName?: string | null;
  actionExtraValue?: string;
  label?: string;
};

export type AiSequenceStepProposal = {
  order?: number;
  emailTemplateId?: string | null;
  emailTemplateName?: string | null;
  subject?: string;
  body?: string;
  sendMode?: string;
  delayDays?: number | null;
  scheduledDate?: string | null;
};

/** "Add an email sequence" (New-work draft). */
export type AiSequenceProposal = {
  name?: string;
  templateId?: string | null;
  templateName?: string | null;
  fromUserId?: string;
  fromEmail?: string;
  fromName?: string | null;
  toEmail?: string;
  stopScope?: string;
  replyStatusId?: string | null;
  replyStatusName?: string | null;
  notifyUserIds?: string[];
  steps?: AiSequenceStepProposal[];
  emailCount?: number;
  label?: string;
};

export type AiClientProposalFields = {
  type?: "organization" | "individual";
  name?: string;
  companyName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  contactPersonName?: string;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  contactType?: string;
};

export type AiProposal =
  | {
      kind: "create_work";
      summary: string;
      fields: AiWorkProposalFields;
      warnings?: string[];
      candidates?: Record<string, unknown>;
    }
  | {
      kind: "create_client";
      summary: string;
      fields: AiClientProposalFields;
      warnings?: string[];
      candidates?: { possibleDuplicates?: AiProposalRef[] };
    }
  | {
      /** Actions on existing work, billing and time (2026-10-26); fields in features/ai/ask/actionProposal.utils.ts. */
      kind:
        | "start_sequence"
        | "repeat_work"
        | "create_invoice"
        | "record_payment"
        | "add_time"
        | "start_timer";
      summary: string;
      fields: Record<string, unknown>;
      warnings?: string[];
      candidates?: Record<string, unknown>;
    };

/** Mail page: sort inbox emails (one AI use per call, max 30 emails). */
export type AiEmailIntent = "reply" | "review" | "docs" | "payment" | "fyi";

export type AiEmailListItem = {
  id: string;
  from: string;
  fromEmail?: string;
  subject: string;
  snippet: string;
  date?: string;
  hasAttachments?: boolean;
  fromClient?: boolean;
};

export type AiEmailTriageItem = {
  id: string;
  intent: AiEmailIntent;
  hasDocuments: boolean;
  /** Follows the sender's name: "is asking you to confirm the deadline". */
  brief: string;
};

export type AiEmailMessage = {
  id?: string;
  from?: string;
  fromEmail?: string;
  to?: string;
  date?: string;
  subject?: string;
  /** HTML or plain text; the server turns it into plain text. */
  body?: string;
  attachments?: string[];
  /** Sent by the signed-in person. */
  sent?: boolean;
};

export type AiEmailStepKind =
  | "reply"
  | "create_work"
  | "add_to_work"
  | "open_client"
  | "open_work"
  | "note";

/** Mail page: summary, next steps and quick replies for one email. */
export type AiEmailInsights = {
  intent: AiEmailIntent;
  hasDocuments: boolean;
  summary: string;
  points: string[];
  nextSteps: { label: string; kind: AiEmailStepKind }[];
  /** `text` is plain text like Write with AI. */
  replies: { label: string; text: string }[];
  attachmentLabels: { name: string; label: string }[];
  generatedAt: string;
};

export type AiEmailInsightsRequest = {
  orgId: string;
  email: AiEmailMessage;
  /** Earlier emails with the same person, newest first (max 5). */
  thread?: AiEmailMessage[];
  customerId?: string;
  workId?: string;
};

// ---------------------------------------------------------------- AI reporting (2026-10-25)

export type AiReportCellKind =
  | "text"
  | "number"
  | "money"
  | "hours"
  | "date"
  | "percent";

export type AiReportColumn = {
  key: string;
  label: string;
  kind: AiReportCellKind;
};

export type AiReportRow = {
  key: string;
  cells: Record<string, string | number | null>;
  /** In-app path ("/..."), e.g. the client page. */
  link?: string;
};

export type AiReportTable = {
  name: string;
  columns: AiReportColumn[];
  rows: AiReportRow[];
};

/** What a report shows, sent with "Ask about this report" (data only). */
export type AiReportData = {
  title: string;
  path: string;
  description?: string;
  filters?: string;
  kpis?: { label: string; value: string }[];
  tables: AiReportTable[];
};

export type AiReportAskRequest = {
  orgId: string;
  question: string;
  history?: { question: string; answer: string }[];
  report: AiReportData;
};

export type AiReportAskResponse = {
  answer: string;
  /** Rows copied from the sent data (never numbers written by the AI). */
  table: AiReportTable | null;
  /** Row keys for "Show only these rows in the report". */
  focusKeys: string[];
};

export type AiReportSpecRequest = {
  orgId: string;
  request: string;
  /** The report being changed with "Change with AI". */
  current?: unknown;
};

export type AiReportInsightRequest = {
  orgId: string;
  title: string;
  request?: string;
  filters?: string;
  columns: AiReportColumn[];
  rows: { label: string; cells: Record<string, number | string | null> }[];
  totals?: Record<string, number | null>;
};

/** "Your day" briefing on Home (backend GET api/ai/briefing). */
export type AiBriefingItem = {
  kind: "work" | "client";
  id: string;
  title: string;
  /** Work: the client's name. Client: "Organization" / "Individual". */
  subtitle?: string | null;
  reason: string;
  link: string;
  /** Work: YYYY-MM-DD in the person's time zone. */
  dueDate?: string | null;
  // Waiting on clients only.
  workId?: string | null;
  workTitle?: string | null;
  workLink?: string | null;
  email?: string | null;
  customerId?: string | null;
  businessContactId?: string | null;
  contactId?: string | null;
};

export type AiBriefingPerson = {
  userId: string;
  name: string;
  dueThisWeek: number;
  overdue: number;
  link: string;
};

export type AiBriefing = {
  view: "my" | "firm";
  date: string;
  generatedAt: string;
  aiWritten: boolean;
  summary: string;
  doFirst: AiBriefingItem[];
  waiting: AiBriefingItem[];
  atRisk: AiBriefingItem[];
  busiest: AiBriefingPerson[];
  counts: { doFirst: number; waiting: number; atRisk: number };
  notice?: string | null;
  limitedToAssignedClients?: boolean;
};

export type AiBriefingArgs = {
  orgId: string;
  view: "my" | "firm";
  /** 0 = today's briefing (cached on the server); n > 0 = Refresh pressed n times. */
  refresh?: number;
  /** false = plain lists only, never an AI call (Home's collapsed line). */
  ai?: boolean;
};

export const aiSlice = createApi({
  reducerPath: "aiSlice",
  tagTypes: ["AiStatus"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getAiStatus: build.query<AiStatus, { orgId: string }>({
      query: ({ orgId }) => ({ url: "api/ai/status", params: { orgId } }),
      providesTags: ["AiStatus"],
    }),
    updateAiSettings: build.mutation<
      AiStatus,
      { orgId: string; enabled: boolean }
    >({
      query: (body) => ({ url: "api/ai/settings", method: "PUT", body }),
      invalidatesTags: ["AiStatus"],
    }),
    aiWrite: build.mutation<AiWriteResponse, AiWriteRequest>({
      query: (body) => ({ url: "api/ai/write", method: "POST", body }),
      // Refresh "used today".
      invalidatesTags: ["AiStatus"],
    }),
    aiWorkSummary: build.mutation<AiWorkSummaryResponse, { workId: string }>({
      // The browser's time zone, so "today" and the dates are the person's own (WITEM-044).
      query: (body) => ({
        url: "api/ai/work-summary",
        method: "POST",
        body: { ...body, timeZone: aiTimeZone() },
      }),
      invalidatesTags: ["AiStatus"],
    }),
    aiHelpChat: build.mutation<AiHelpChatResponse, AiHelpChatRequest>({
      query: (body) => ({ url: "api/ai/help-chat", method: "POST", body }),
      invalidatesTags: ["AiStatus"],
    }),
    aiAsk: build.mutation<AiAskResponse, AiAskRequest>({
      query: (body) => ({ url: "api/ai/ask", method: "POST", body }),
      // Refresh "used today" after an answer.
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    aiEmailTriage: build.mutation<
      { items: AiEmailTriageItem[] },
      { orgId: string; emails: AiEmailListItem[] }
    >({
      query: (body) => ({ url: "api/ai/email-triage", method: "POST", body }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    aiEmailInsights: build.mutation<AiEmailInsights, AiEmailInsightsRequest>({
      query: (body) => ({ url: "api/ai/email-insights", method: "POST", body }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    aiReportAsk: build.mutation<AiReportAskResponse, AiReportAskRequest>({
      query: (body) => ({ url: "api/ai/report-ask", method: "POST", body }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    /** `spec` is checked again in the browser (normalizeSpec). */
    aiReportSpec: build.mutation<{ spec: unknown }, AiReportSpecRequest>({
      query: (body) => ({
        url: "api/ai/report-spec",
        method: "POST",
        body: { ...body, timeZone: aiTimeZone() },
      }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    aiReportInsight: build.mutation<{ text: string }, AiReportInsightRequest>({
      query: (body) => ({ url: "api/ai/report-insight", method: "POST", body }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    getAiBriefing: build.query<AiBriefing, AiBriefingArgs>({
      query: ({ orgId, view, refresh, ai }) => ({
        url: "api/ai/briefing",
        params: {
          orgId,
          view,
          timeZone: aiTimeZone(),
          ...(refresh ? { refresh: true } : {}),
          ...(ai === false ? { ai: false } : {}),
        },
      }),
    }),
    // Works even when AI is off or not set up.
    sendBugReport: build.mutation<AiBugReportResponse, AiBugReportRequest>({
      query: (body) => ({ url: "api/ai/bug-report", method: "POST", body }),
    }),
  }),
});

export const {
  useGetAiStatusQuery,
  useUpdateAiSettingsMutation,
  useAiWriteMutation,
  useAiWorkSummaryMutation,
  useAiHelpChatMutation,
  useSendBugReportMutation,
  useAiAskMutation,
  useAiEmailTriageMutation,
  useAiEmailInsightsMutation,
  useAiReportAskMutation,
  useAiReportSpecMutation,
  useAiReportInsightMutation,
  useGetAiBriefingQuery,
} = aiSlice;
